import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { GetParticipantDataQuery as GetParticipantData } from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import EditNoteMutation from './EditNoteMutation';
import GetParticipantDataQuery from '../GetParticipantDataQuery';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useValidation from '../../../../common/useValidation';

type Note = GetParticipantData['project']['participant']['notes'][0];

const editNodeSchema = yup.object().shape({
  content: yup.string().required(),
});

const useEditNoteModal = (note: Note, refetchQueries?: DocumentNode[]) => {
  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal maxWidth={600} close={closeModal}>
        <EditNoteForm
          note={note}
          onClose={() => closeModal()}
          refetchQueries={refetchQueries}
        />
      </UI.Modal>
    ),
    [note, refetchQueries],
  );

  return [openModal, closeModal];
};

export interface EditNoteFormProps {
  note: Note;
  onClose: () => void;
  refetchQueries?: DocumentNode[];
}

/**
 * Render form with a textarea for adding a note to a participant.
 */
export const EditNoteForm = ({ note, onClose, refetchQueries = [GetParticipantDataQuery] }: EditNoteFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    values: {
      content: note.content,
    },
  });

  const notifier = useNotifier();

  const [editNote, { loading, error }] = useMutation(EditNoteMutation, {
    variables: {
      input: {
        id: note.id,
        content: form.values.content,
      },
    },
    refetchQueries,
    onCompleted: () => {
      notifier.success(t('notifications.note_saved'));
      onClose?.();
    },
  });

  const { valid, errors } = useValidation({
    schema: editNodeSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editNote()}>
      <UI.FormGrid>
        <UI.H4>{t('edit_note')}</UI.H4>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.FormGrid>
          <UI.InputGroup sc={{ valid: !errors.content, touched: form.getTouched('content') }}>
            <UI.InputLabel htmlFor="note_content">
              {t('note')}
            </UI.InputLabel>
            <UI.TextArea
              {...form.textarea('content')}
              autoFocus
              id="note_content"
            />
            <UI.ErrorMessages attribute={t('note')} errors={errors.content} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('edit_note')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default useEditNoteModal;
